import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Building2, Search, Plus, Edit, Trash2, X, Eye, Filter, FileText, FileSpreadsheet, FileOutput, RefreshCcw, LayoutGrid, Shield, Layers, Building, Car, Laptop, Armchair, TrendingDown, DollarSign, Calendar, ChevronDown, ChevronUp, Upload, Download, List, LayoutList, CheckCircle, XCircle, MapPin, Hash, Package } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { mapComptabiliteStatusOptions, useComptabiliteTableColumnVisibility, useComptabiliteListRowSelection, ComptabiliteBulkDeleteFilterAction, COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS } from './utils/comptabiliteListPageUtils';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
} from '../../products/saudi/components/QawaemTableSelection';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { exportToCSV, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencySuffix, getBrandLocale } from '../../shared/utils/brandCurrency';
import { SOCPA_DEPRECIATION } from './utils/saudiTaxConfig';
import { getAssets, createAsset, updateAsset, deleteAsset } from '../../shared/utils/comptabiliteService';
import { logger } from '../../shared/utils/logger';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import '../../shared/styles/GPSTrackingDatePicker.css';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';

const DROPDOWN_PANEL_STYLE = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  right: 0,
  zIndex: 99999,
  backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
  boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' };

const EMPTY_FORM = {
  assetNumber: '',
  name: '',
  category: '',
  status: '',
  purchaseValue: '',
  currentValue: '',
  purchaseDate: '',
  depreciationRate: '',
  depreciationMethod: 'straight_line',
  location: '',
  supplier: '',
  serialNumber: '',
  description: '' };

const CATEGORY_DEFS = SOCPA_DEPRECIATION.categories.map((c) => ({
  id: c.id,
  nameKey: c.labelKey || `cat_${c.id}`,
  labelAr: c.labelAr,
  defaultRate: c.defaultRate,
  usefulLifeYears: c.usefulLifeYears,
  icon: c.id === 'buildings' ? Building : c.id === 'vehicles' ? Car : c.id === 'it_equipment' ? Laptop : Armchair,
  color: c.id === 'buildings' ? 'from-blue-500 to-blue-600' : c.id === 'vehicles' ? 'from-green-500 to-green-600' : c.id === 'machinery' ? 'from-purple-500 to-purple-600' : 'from-orange-500 to-orange-600' }));

const STATUS_OPTIONS = [
  { value: 'active', labelKey: 'status_active', icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
  { value: 'maintenance', labelKey: 'status_maintenance', icon: Filter, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  { value: 'disposed', labelKey: 'status_disposed', icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
];

const transformAsset = (asset) => ({
  id: asset.id,
  category: asset.category,
  name: asset.name,
  purchaseValue: asset.purchase_price,
  currentValue: asset.current_value ?? asset.net_book_value,
  purchaseDate: asset.purchase_date,
  depreciationRate: asset.depreciation_rate,
  description: asset.description || '',
  assetNumber: asset.asset_number,
  location: asset.location,
  supplier: asset.supplier,
  serialNumber: asset.serial_number,
  status: asset.status,
  accumulatedDepreciation: asset.accumulated_depreciation,
  netBookValue: asset.net_book_value,
  created_at: asset.created_at,
  updated_at: asset.updated_at });

const ASSETS_LIST_COLUMNS = [
  { key: 'assetNumber', labelKey: 'col_asset_number' },
  { key: 'name', labelKey: 'col_name' },
  { key: 'category', labelKey: 'col_category' },
  { key: 'purchaseValue', labelKey: 'col_purchase_value' },
  { key: 'currentValue', labelKey: 'col_current_value' },
  { key: 'depreciation', labelKey: 'col_depreciation' },
  { key: 'purchaseDate', labelKey: 'col_purchase_date' },
  { key: 'status', labelKey: 'col_status' },
];

const AssetsPage = () => {
  const { t, i18n } = useTranslation();
  const { isDashboardLightMode } = useGlobalTheme();
  const p = (key, opts) => t(`modules.comptabilite.pages.assetsPage.${key}`, opts);

  const getCategoryLabel = (categoryId) => {
    const category = CATEGORY_DEFS.find((c) => c.id === categoryId);
    if (category?.labelAr) return category.labelAr;
    return category ? p(category.nameKey) : categoryId;
  };

  const getStatusLabel = (status) => {
    const config = STATUS_OPTIONS.find((s) => s.value === status);
    return config ? p(config.labelKey) : status;
  };

  const categorySelectOptions = useMemo(
    () => CATEGORY_DEFS.map((c) => ({ value: c.id, label: c.labelAr || p(c.nameKey), isDefault: true })),
    [t]
  );

  const statusSelectOptions = useMemo(
    () => STATUS_OPTIONS.map((s) => ({ value: s.value, label: p(s.labelKey), isDefault: true })),
    [t]
  );

  const formatDate = (dateStr) => (dateStr ? new Date(dateStr).toLocaleDateString(getBrandLocale(i18n.language)) : p('na'));

  const exportHeaders = useMemo(
    () => [
      { key: 'assetNumber', label: p('col_asset_number') },
      { key: 'name', label: p('col_name') },
      { key: 'category', label: p('col_category') },
      { key: 'purchaseValue', label: p('col_purchase_value') },
      { key: 'currentValue', label: p('col_current_value') },
      { key: 'purchaseDate', label: p('export_col_purchase_date') },
      { key: 'status', label: p('col_status') },
    ],
    [t]
  );
  const [assets, setAssets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [viewingAsset, setViewingAsset] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [mainViewMode, setMainViewMode] = useState('all_assets');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  useEffect(() => {
    if (viewMode === 'table' && String(searchTerm || '').trim()) {
      setSearchTerm('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { filters: columnFilters, setFilter: setColumnFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  const columnFilterRules = useMemo(() => [
    textColumnRule('assetNumber', (row) => row.assetNumber ?? ''),
    textColumnRule('name', (row) => row.name ?? ''),
    textColumnRule('category', (row) => row.category ?? ''),
    textColumnRule('purchaseValue', (row) => String(row.purchaseValue ?? '')),
    textColumnRule('currentValue', (row) => String(row.currentValue ?? '')),
    textColumnRule('purchaseDate', (row) => row.purchaseDate ?? ''),
    textColumnRule('status', (row) => row.status ?? ''),
  ], []);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [assetFormTab, setAssetFormTab] = useState('generalInfo');

  const assetFormTabs = useMemo(() => ([
    { id: 'generalInfo', label: p('section_identification'), icon: Hash, iconClassName: 'text-emerald-400' },
    { id: 'detailsInfo', label: p('section_location_supplier'), icon: MapPin, iconClassName: 'text-red-400' },
  ]), [p]);

  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);

  useEffect(() => { loadAssets(); }, [refreshTrigger]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAssets = async () => {
    try {
      setIsLoading(true);
      const assetsData = await getAssets({});
      setAssets(sortRecordsNewestFirst(assetsData.map(transformAsset)));
    } catch (err) {
      logger.error('Error loading assets:', err);
      const savedAssets = localStorage.getItem('companyAssets');
      if (savedAssets) setAssets(sortRecordsNewestFirst(JSON.parse(savedAssets)));
    } finally {
      setIsLoading(false);
    }
  };

  const stats = useMemo(() => {
    const totalPurchase = assets.reduce((sum, a) => sum + (parseFloat(a.purchaseValue) || 0), 0);
    const totalValue = assets.reduce((sum, a) => sum + (parseFloat(a.currentValue) || 0), 0);
    const byCategory = Object.fromEntries(
      CATEGORY_DEFS.map((c) => [c.id, assets.filter((a) => a.category === c.id).length])
    );
    return {
      total: assets.length,
      buildings: byCategory.buildings || 0,
      vehicles: byCategory.vehicles || 0,
      machinery: byCategory.machinery || 0,
      ...byCategory,
      totalValue,
      totalPurchase,
      depreciationPct: totalPurchase > 0 ? ((1 - totalValue / totalPurchase) * 100).toFixed(1) : '0.0' };
  }, [assets]);

  const getValueDistribution = () => {
    const highMin = 100000;
    const lowMax = 10000;
    const high = assets.filter((a) => (parseFloat(a.purchaseValue) || 0) >= highMin).length;
    const medium = assets.filter((a) => {
      const v = parseFloat(a.purchaseValue) || 0;
      return v >= lowMax && v < highMin;
    }).length;
    const low = assets.filter((a) => (parseFloat(a.purchaseValue) || 0) < lowMax).length;
    return [
      { nameKey: 'value_high', filterKey: 'high', count: high, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      { nameKey: 'value_medium', filterKey: 'medium', count: medium, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
      { nameKey: 'value_low', filterKey: 'low', count: low, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
    ];
  };

  const assetDistribution = useMemo(() => ({
    all_assets: {
      titleKey: 'dist_overview',
      data: CATEGORY_DEFS.slice(0, 4).map((cat) => ({
        nameKey: cat.nameKey,
        labelAr: cat.labelAr,
        filterKey: cat.id,
        count: stats[cat.id] || 0,
        color: `bg-gradient-to-r ${cat.color}` })) },
    par_categorie: {
      titleKey: 'dist_by_category',
      data: CATEGORY_DEFS.map((cat) => ({
        nameKey: cat.nameKey,
        labelAr: cat.labelAr,
        filterKey: cat.id,
        count: assets.filter((a) => a.category === cat.id).length,
        color: `bg-gradient-to-r ${cat.color}` })) },
    par_statut: {
      titleKey: 'dist_by_status',
      data: STATUS_OPTIONS.map((s) => ({
        nameKey: s.labelKey,
        filterKey: s.value,
        count: assets.filter((a) => a.status === s.value).length,
        color: s.value === 'active' ? 'bg-gradient-to-r from-green-500 to-green-600' : s.value === 'maintenance' ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' : 'bg-gradient-to-r from-red-500 to-red-600' })) },
    par_valeur: {
      titleKey: 'dist_by_value',
      data: getValueDistribution() } }), [assets, stats]);

  const distributionInfo = assetDistribution[mainViewMode] || assetDistribution.all_assets;

  const mainTabs = useMemo(() => [
    { id: 'all_assets', nameKey: 'tab_overview', icon: LayoutGrid },
    { id: 'par_categorie', nameKey: 'tab_by_category', icon: Shield },
    { id: 'par_statut', nameKey: 'tab_by_status', icon: CheckCircle },
    { id: 'par_valeur', nameKey: 'tab_by_value', icon: TrendingDown },
  ], []);

  const quickFilterButtons = useMemo(() => [
    { id: 'all', nameKey: 'filter_all', icon: Layers },
    ...CATEGORY_DEFS.slice(0, 4).map((cat) => ({
      id: cat.id,
      nameKey: cat.nameKey,
      labelAr: cat.labelAr,
      icon: cat.icon })),
  ], []);

  const statCards = useMemo(() => [
    { labelKey: 'stat_total', value: stats.total, icon: Building2, color: 'from-blue-500 to-blue-600' },
    { labelKey: 'stat_buildings', labelAr: 'المباني', value: stats.buildings || 0, icon: Building, color: 'from-blue-500 to-blue-600' },
    { labelKey: 'stat_vehicles', labelAr: 'المركبات', value: stats.vehicles || 0, icon: Car, color: 'from-green-500 to-green-600' },
    { labelKey: 'stat_total_value', value: formatBrandMoney(stats.totalValue), icon: DollarSign, color: 'from-indigo-500 to-indigo-600' },
    { labelKey: 'stat_depreciation', value: `${stats.depreciationPct}%`, icon: TrendingDown, color: 'from-red-500 to-red-600' },
  ], [stats]);

  const filteredAssets = useMemo(() => {
    const base = assets.filter((asset) => {
      if (statusFilter !== 'all' && asset.status !== statusFilter) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (!asset.name?.toLowerCase().includes(term)
          && !asset.assetNumber?.toLowerCase().includes(term)
          && !String(asset.category || '').toLowerCase().includes(term)) {
          return false;
        }
      }
      if (mainViewMode === 'all_assets' && quickFilter !== 'all' && asset.category !== quickFilter) return false;
      if (activeDistributionFilter) {
        if (mainViewMode === 'par_categorie' || mainViewMode === 'all_assets') {
          if (asset.category !== activeDistributionFilter) return false;
        } else if (mainViewMode === 'par_statut') {
          if (asset.status !== activeDistributionFilter) return false;
        } else if (mainViewMode === 'par_valeur') {
          const value = parseFloat(asset.purchaseValue) || 0;
          if (activeDistributionFilter === 'high' && value < 100000) return false;
          if (activeDistributionFilter === 'medium' && (value < 10000 || value >= 100000)) return false;
          if (activeDistributionFilter === 'low' && value >= 10000) return false;
        }
      }
      return true;
    });
    return applyColumnFilters(base, columnFilters, columnFilterRules);
  }, [assets, statusFilter, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, columnFilters, columnFilterRules]);

  const {
    selectedRecords: selectedAssets,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord: handleSelectAsset,
    clearSelection,
  } = useComptabiliteListRowSelection(filteredAssets, (row) => row.id);

  const currentAssets = filteredAssets;

  const {
    qawaemColumnDefs,
    handleToggleColumn,
    qawaemVisibleColumnCount,
    isColVisible,
    visibleDataColumnCount,
  } = useComptabiliteTableColumnVisibility(ASSETS_LIST_COLUMNS, p);
  const assetsTableColSpan = visibleDataColumnCount + 2;

  const footerTotals = useMemo(() => ({
    purchase: filteredAssets.reduce((sum, a) => sum + (parseFloat(a.purchaseValue) || 0), 0),
    current: filteredAssets.reduce((sum, a) => sum + (parseFloat(a.currentValue) || 0), 0),
  }), [filteredAssets]);

  const statusBadgeOptions = useMemo(() => mapComptabiliteStatusOptions(STATUS_OPTIONS, p), [p]);

  const getCategoryBadge = (categoryId) => {
    const cat = CATEGORY_DEFS.find((c) => c.id === categoryId);
    const label = cat ? (cat.labelAr || p(cat.nameKey)) : categoryId;
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/10 text-white/80">{label}</span>;
  };

  const getStatusBadge = (status) => (
    <QawaemClickableStatusBadge
      status={status}
      recordId={status}
      options={statusBadgeOptions}
      onChange={() => {}}
      title={p('col_status')}
    />
  );

  const renderColumnFilter = (key, label, center = false) => (
    <TableColumnHeaderFilter
      columnKey={key}
      label={label}
      value={columnFilters[key] || ''}
      onChange={(v) => setColumnFilter(key, v)}
      editingKey={editingKey}
      onEditingKeyChange={setEditingKey}
      align={center ? 'center' : 'start'}
      thClassName="text-white"
    />
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        asset_number: formData.assetNumber,
        name: formData.name,
        category: formData.category,
        status: formData.status,
        purchase_price: formData.purchaseValue,
        current_value: formData.currentValue,
        purchase_date: formData.purchaseDate,
        depreciation_rate: formData.depreciationRate,
        location: formData.location,
        supplier: formData.supplier,
        serial_number: formData.serialNumber,
        description: formData.description,
      };
      if (editingAsset) {
        await updateAsset(editingAsset.id, payload);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'الأصل' }));
      } else {
        await createAsset(payload);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'الأصل' }));
      }
      setRefreshTrigger((k) => k + 1);
      resetForm();
    } catch (err) {
      logger.error('Error saving asset:', err);
      notifyError(showNotify, p('save_error', { message: err.message }));
    }
  };

  const handleEdit = (asset) => {
    setEditingAsset(asset);
    setFormData({
      assetNumber: asset.assetNumber || '',
      name: asset.name || '',
      category: asset.category || '',
      status: asset.status || '',
      purchaseValue: asset.purchaseValue || '',
      currentValue: asset.currentValue || '',
      purchaseDate: asset.purchaseDate || '',
      depreciationRate: asset.depreciationRate || '',
      location: asset.location || '',
      supplier: asset.supplier || '',
      serialNumber: asset.serialNumber || '',
      description: asset.description || '',
    });
    setAssetFormTab('generalInfo');
    setShowModal(true);
  };

  const handleView = (asset) => {
    setViewingAsset(asset);
    setShowViewModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await deleteAsset(id);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'الأصل' }));
        setRefreshTrigger((k) => k + 1);
      } catch (err) {
        logger.error('Error deleting asset:', err);
        notifyError(showNotify, p('save_error', { message: err.message }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedAssets.length) return;
    if (window.confirm(p('bulk_delete_confirm', { count: selectedAssets.length }))) {
      try {
        const deleteCount = selectedAssets.length;
        await Promise.all(selectedAssets.map((id) => deleteAsset(id)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'الأصول' }));
        clearSelection();
        setRefreshTrigger((k) => k + 1);
      } catch (err) {
        logger.error('Error bulk deleting assets:', err);
        notifyError(showNotify, p('save_error', { message: err.message }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM });
    setEditingAsset(null);
    setShowModal(false);
    setAssetFormTab('generalInfo');
  };

  return (
    <div className="space-y-6 employees-container">
      <div className="flex justify-between items-start list-toolbar-mobile-layout">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <ComptabiliteBulkDeleteFilterAction
              count={selectedAssets.length}
              onClick={handleBulkDelete}
              label={p('delete_with_count', { count: selectedAssets.length, defaultValue: `حذف (${selectedAssets.length})` })}
              title={p('delete_with_count', { count: selectedAssets.length, defaultValue: `حذف (${selectedAssets.length})` })}
            />
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ) : null}
            <FacturationViewModeToggle
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              isLight={isDashboardLightMode}
              cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
              tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
            />
          </div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Building2 className="h-8 w-8 text-emerald-400" />
            {p('title')}
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3 shrink-0">
          <button type="button" onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold flex items-center gap-2`}>
            <Filter className="h-5 w-5" /><span className="hidden sm:inline">{p('advanced_options')}</span>
          </button>
          <button type="button" className="bg-gradient-to-r from-emerald-500 to-emerald-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2" onClick={() => { setEditingAsset(null); setShowModal(true); }}>
            <Plus className="h-5 w-5" /><span>{p('new_asset')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.slice(0, 4).map((stat, i) => (
          <motion.div key={stat.labelKey} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <p className="text-white/60 text-sm">{stat.labelAr || p(stat.labelKey)}</p>
            <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 space-y-4 overflow-hidden">
            <div className="flex flex-wrap gap-2">
              {mainTabs.map((tab) => (
                <button key={tab.id} type="button" onClick={() => { setMainViewMode(tab.id); setActiveDistributionFilter(null); }} className={`px-4 py-2 rounded-xl text-sm font-medium ${mainViewMode === tab.id ? 'bg-blue-500/30 text-white' : 'bg-white/5 text-white/60'}`}>
                  {p(tab.nameKey)}
                </button>
              ))}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
              {distributionInfo.data.map((item) => (
                <button key={item.filterKey || item.nameKey} type="button" onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.nameKey) ? null : (item.filterKey || item.nameKey))} className="bg-white/5 rounded-xl p-4 border border-white/10 text-left">
                  <div className="flex justify-between"><span className="text-white/80">{item.labelAr || p(item.nameKey)}</span><span className="text-white font-semibold">{item.count}</span></div>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {isColVisible('assetNumber') ? renderColumnFilter('assetNumber', p('col_asset_number')) : null}
                  {isColVisible('name') ? renderColumnFilter('name', p('col_name')) : null}
                  {isColVisible('category') ? renderColumnFilter('category', p('col_category')) : null}
                  {isColVisible('purchaseValue') ? renderColumnFilter('purchaseValue', p('col_purchase_value')) : null}
                  {isColVisible('currentValue') ? renderColumnFilter('currentValue', p('col_current_value')) : null}
                  {isColVisible('depreciation') ? <th className="px-6 py-4 text-left text-white font-semibold">{p('col_depreciation')}</th> : null}
                  {isColVisible('purchaseDate') ? renderColumnFilter('purchaseDate', p('col_purchase_date')) : null}
                  {isColVisible('status') ? renderColumnFilter('status', p('col_status'), true) : null}
                  <QawaemTableHeaderActionsCell label={p('col_actions')} columns={qawaemColumnDefs} onToggle={handleToggleColumn} visibleCount={qawaemVisibleColumnCount} totalCount={qawaemColumnDefs.length} thClassName={COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS} />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {currentAssets.length > 0 ? currentAssets.map((asset, index) => (
                  <tr key={asset.id} className={`hover:bg-white/5 transition-colors ${selectedAssets.includes(asset.id) ? 'bg-white/10' : ''}`}>
                    <QawaemTableSelectRowCell
                      checked={selectedAssets.includes(asset.id)}
                      onChange={() => handleSelectAsset(asset.id)}
                    />
                    {isColVisible('assetNumber') ? <td className="px-6 py-4 text-white font-semibold">{asset.assetNumber || '—'}</td> : null}
                    {isColVisible('name') ? <td className="px-6 py-4 text-white font-semibold">{asset.name}</td> : null}
                    {isColVisible('category') ? <td className="px-6 py-4"><div className="flex justify-start">{getCategoryBadge(asset.category)}</div></td> : null}
                    {isColVisible('purchaseValue') ? <td className="px-6 py-4 text-white/80">{formatBrandMoney(asset.purchaseValue)}</td> : null}
                    {isColVisible('currentValue') ? <td className="px-6 py-4 text-white font-semibold">{formatBrandMoney(asset.currentValue)}</td> : null}
                    {isColVisible('depreciation') ? <td className="px-6 py-4"><span className="text-red-400 font-semibold">-{formatBrandMoney((parseFloat(asset.purchaseValue) || 0) - (parseFloat(asset.currentValue) || 0))}</span></td> : null}
                    {isColVisible('purchaseDate') ? <td className="px-6 py-4 text-white/80">{asset.purchaseDate ? new Date(asset.purchaseDate).toLocaleDateString(getBrandLocale(i18n.language)) : '—'}</td> : null}
                    {isColVisible('status') ? <td className="px-6 py-4 text-center"><div className="flex justify-center">{getStatusBadge(asset.status)}</div></td> : null}
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-1">
                        <button type="button" onClick={() => handleView(asset)} className="p-1.5" title={p('action_view')}><Eye size={16} className="text-blue-400" /></button>
                        <button type="button" onClick={() => handleEdit(asset)} className="p-1.5" title={p('action_edit')}><Edit size={16} className="text-green-400" /></button>
                        <button type="button" onClick={() => handleDelete(asset.id)} className="p-1.5" title={p('action_delete')}><Trash2 size={16} className="text-red-400" /></button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={assetsTableColSpan} className="text-center py-12 text-white/60"><Building2 size={48} className="mx-auto mb-4 text-white/30" /><p>{p('empty_title')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={assetsTableColSpan} className="px-6 py-4">
                    <div className="flex flex-wrap items-center justify-between gap-4 text-white/70 text-sm">
                      <span>{p('footer_total', { count: filteredAssets.length })}</span>
                      <span>{p('footer_purchase_value')} <span className="text-white font-semibold">{formatBrandMoney(footerTotals.purchase)}</span></span>
                      <span>{p('footer_current_value')} <span className="text-white font-semibold">{formatBrandMoney(footerTotals.current)}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {currentAssets.length > 0 ? currentAssets.map((asset, index) => (
              <motion.div key={asset.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-4 mb-4 flex-wrap gap-2">
                      <h3 className="text-xl font-bold text-white flex items-center gap-2"><Building2 size={20} className="text-emerald-400" />{asset.name}</h3>
                      {getCategoryBadge(asset.category)}
                      {getStatusBadge(asset.status)}
                      {asset.purchaseDate && <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">{new Date(asset.purchaseDate).toLocaleDateString(getBrandLocale(i18n.language))}</span>}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('card_identification')}</h5>
                        <div className="space-y-1 text-sm text-white/70">
                          <div><span className="font-medium">{p('label_asset_number')}</span> {asset.assetNumber || p('na')}</div>
                          <div><span className="font-medium">{p('label_name')}</span> {asset.name}</div>
                          <div><span className="font-medium">{p('label_serial_number')}</span> {asset.serialNumber || p('na')}</div>
                        </div>
                      </div>
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('card_financial_values')}</h5>
                        <div className="space-y-1 text-sm text-white/70">
                          <div><span className="font-medium">{p('label_purchase')}</span> {formatBrandMoney(asset.purchaseValue)}</div>
                          <div><span className="font-medium">{p('label_current')}</span> {formatBrandMoney(asset.currentValue)}</div>
                          <div className="text-red-400"><span className="font-medium">{p('label_depreciation')}</span> -{formatBrandMoney((parseFloat(asset.purchaseValue) || 0) - (parseFloat(asset.currentValue) || 0))}</div>
                          <div><span className="font-medium">{p('label_rate')}</span> {asset.depreciationRate || 0}%</div>
                        </div>
                      </div>
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('card_location')}</h5>
                        <div className="space-y-1 text-sm text-white/70">
                          <div className="flex items-center gap-2"><MapPin size={16} className="text-amber-400" /><span>{asset.location || p('location_not_set')}</span></div>
                          <div><span className="font-medium">{p('label_supplier')}</span> {asset.supplier || p('na')}</div>
                        </div>
                      </div>
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('card_description')}</h5>
                        <div className="text-sm text-white/70">{asset.description || <span className="text-white/40 italic">{p('no_description')}</span>}</div>
                      </div>
                    </div>
                  </div>
                  <QawaemErpRowActions
                    compact
                    size={14}
                    onView={() => handleView(asset)}
                    onEdit={() => handleEdit(asset)}
                    onDelete={() => handleDelete(asset.id)}
                    viewTitle={t('common.view')}
                    editTitle={p('action_edit')}
                    deleteTitle={p('action_delete')}
                  />
                </div>
              </motion.div>
            )) : (
              <div className="col-span-full text-center py-12"><Building2 size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty_title')}</p></div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal — tabbed sections (Products.js pattern) */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={`backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`} style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '90%', maxWidth: '1200px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }} onClick={(e) => e.stopPropagation()}>
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold flex items-center gap-2 ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}><Building2 className="h-6 w-6 text-blue-400" />{editingAsset ? p('modal_edit') : p('modal_new')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="asset-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"><CheckCircle size={18} />{editingAsset ? p('btn_save') : p('btn_create')}</button>
                <button type="button" onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}><X size={24} /></button>
              </div>
            </div>
            <div className={`shrink-0 px-6 pt-3 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <div className="flex flex-wrap gap-1" role="tablist" aria-label={editingAsset ? p('modal_edit') : p('modal_new')}>
                {assetFormTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = assetFormTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setAssetFormTab(tab.id)}
                      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${
                        active
                          ? (isDashboardLightMode
                            ? 'bg-[#6e9b6e]/15 text-forest-900 border-b-2 border-[#6e9b6e] -mb-px'
                            : 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px')
                          : (isDashboardLightMode
                            ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80'
                            : 'text-white/55 hover:text-white hover:bg-white/5')
                      }`}
                    >
                      <TabIcon size={16} className={active ? (tab.id === 'financialInfo' ? 'text-yellow-400' : tab.id === 'descriptionInfo' ? 'text-purple-400' : tab.id === 'locationInfo' ? 'text-red-400' : 'text-emerald-400') : ''} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="asset-form" onSubmit={handleSubmit}>
                {assetFormTab === 'generalInfo' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('form_asset_number')}</label><input type="text" value={formData.assetNumber} onChange={(e) => setFormData({ ...formData, assetNumber: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_asset_number')} /></div>
                    <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('form_name')}</label><input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_name')} /></div>
                    <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('form_category')}</label><CustomSelect value={formData.category} onChange={(val) => setFormData({ ...formData, category: val })} options={categorySelectOptions} placeholder={p('placeholder_category')} searchable creatable autoSelect={false} /></div>
                    <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('form_status')}</label><CustomSelect value={formData.status} onChange={(val) => setFormData({ ...formData, status: val })} options={statusSelectOptions} placeholder={p('placeholder_status')} searchable creatable autoSelect={false} /></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('form_purchase_value')}</label><CustomNumberInput step={0.01} value={formData.purchaseValue} onChange={(v) => setFormData({ ...formData, purchaseValue: v })} placeholder={p('placeholder_zero')} /></div>
                    <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('form_current_value')}</label><CustomNumberInput step={0.01} value={formData.currentValue} onChange={(v) => setFormData({ ...formData, currentValue: v })} placeholder={p('placeholder_zero')} /></div>
                    <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('form_depreciation_rate')}</label><CustomNumberInput step={0.01} value={formData.depreciationRate} onChange={(v) => setFormData({ ...formData, depreciationRate: v })} placeholder={p('placeholder_zero')} /></div>
                    <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('form_purchase_date')}</label><CustomDatePicker value={formData.purchaseDate} onChange={(d) => setFormData({ ...formData, purchaseDate: d })} placeholder={p('placeholder_purchase_date')} /></div>
                    </div>
                  </div>
                )}
                {assetFormTab === 'detailsInfo' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('form_location')}</label><input type="text" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_location')} /></div>
                    <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('form_supplier')}</label><input type="text" value={formData.supplier} onChange={(e) => setFormData({ ...formData, supplier: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_supplier')} /></div>
                    <div className="min-w-0"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('form_serial_number')}</label><input type="text" value={formData.serialNumber} onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_serial_number')} /></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="min-w-0 md:col-span-3"><label className={`block w-full text-sm mb-2 font-medium text-right ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>{p('form_description')}</label><textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_description')} /></div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* View Modal */}
      {showViewModal && viewingAsset && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowViewModal(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2"><Eye className="h-6 w-6 text-blue-400" />{p('view_modal_title')}</h2>
              <button type="button" onClick={() => setShowViewModal(false)} className="text-white/60 hover:text-white transition-colors"><X className="h-6 w-6" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><Hash size={18} className="text-emerald-400" />{p('section_identification')}</h3>
                <div className="space-y-2 text-sm text-white/70">
                  <div><span className="text-white/60">{p('label_asset_number')}</span> <span className="text-white">{viewingAsset.assetNumber || p('na')}</span></div>
                  <div><span className="text-white/60">{p('label_name')}</span> <span className="text-white">{viewingAsset.name}</span></div>
                  <div><span className="text-white/60">{p('form_category')}:</span> {getCategoryBadge(viewingAsset.category)}</div>
                  <div><span className="text-white/60">{p('form_status')}:</span> {getStatusBadge(viewingAsset.status)}</div>
                </div>
              </div>
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><DollarSign size={18} className="text-yellow-400" />{p('section_financial_short')}</h3>
                <div className="space-y-2 text-sm text-white/70">
                  <div><span className="text-white/60">{p('view_label_purchase_value')}</span> <span className="text-white font-semibold">{formatBrandMoney(viewingAsset.purchaseValue)}</span></div>
                  <div><span className="text-white/60">{p('view_label_current_value')}</span> <span className="text-white font-semibold">{formatBrandMoney(viewingAsset.currentValue)}</span></div>
                  <div><span className="text-white/60">{p('view_label_depreciation_rate')}</span> {viewingAsset.depreciationRate || 0}%</div>
                  <div><span className="text-white/60">{p('view_label_purchase_date')}</span> {viewingAsset.purchaseDate ? new Date(viewingAsset.purchaseDate).toLocaleDateString(getBrandLocale(i18n.language)) : p('na')}</div>
                </div>
              </div>
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><MapPin size={18} className="text-red-400" />{p('card_location')}</h3>
                <div className="space-y-2 text-sm text-white/70">
                  <div><span className="text-white/60">{p('view_label_location')}</span> {viewingAsset.location || p('na')}</div>
                  <div><span className="text-white/60">{p('view_label_supplier')}</span> {viewingAsset.supplier || p('na')}</div>
                  <div><span className="text-white/60">{p('view_label_serial_number')}</span> {viewingAsset.serialNumber || p('na')}</div>
                </div>
              </div>
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><FileText size={18} className="text-purple-400" />{p('card_description')}</h3>
                <p className="text-sm text-white/70">{viewingAsset.description || p('no_description')}</p>
              </div>
            </div>
            <div className="flex justify-end gap-4 mt-8 pt-6 border-t border-white/10">
              <button type="button" className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition-all border border-white/20" onClick={() => setShowViewModal(false)}>{p('close')}</button>
              <button type="button" className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white rounded-xl font-semibold transition-all flex items-center gap-2 shadow-lg shadow-blue-500/30" onClick={() => { setShowViewModal(false); handleEdit(viewingAsset); }}><Edit className="h-4 w-4" />{p('edit')}</button>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {isLoading && (
        <div className="fixed bottom-4 right-4 bg-blue-500/20 border border-blue-400/40 text-blue-200 px-4 py-2 rounded-xl text-sm backdrop-blur-lg z-50">
          {p('loading')}
        </div>
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default AssetsPage;

